import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    projectType: "",
    projectTypeOther: "",
    timeline: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ✅ VERSION SIMPLE QUI MARCHE
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbwLsIPCCylm4CP9eMZT_HVTaloNMjfFXdKhmvle-zSZ93VWQ1Kinb_7WjIuAQLuEwyNYA/exec",
        {
          method: "POST",
          mode: "no-cors",
          body: JSON.stringify({
            ...formData,
            projectType:
              formData.projectType === "Autre"
                ? formData.projectTypeOther
                : formData.projectType,
          }),
        }
      );

      setSuccess(true);

      setFormData({
        name: "",
        email: "",
        company: "",
        projectType: "",
        projectTypeOther: "",
        timeline: "",
      });
    } catch (error) {
      alert("Erreur d’envoi");
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-2xl border border-white/20 bg-[#0b0f1a]/95 backdrop-blur-xl shadow-[0_20px_80px_rgba(0,0,0,0.9)]">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <div>
            <span className="text-xs text-blue-400">🔥 Audit IA offert</span>
            <h2 className="text-lg font-semibold text-white mt-1">
              Audit gratuit de votre acquisition digitale
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              SEO, Ads, IA • Recommandations sous 24h
            </p>
          </div>

          <button onClick={onClose}>
            <X className="text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* SUCCESS */}
        {success ? (
          <div className="p-8 text-center text-white space-y-3">
            <div className="text-green-400 text-2xl">✔</div>

            <h3 className="text-lg font-semibold">
              Merci, votre demande a bien été envoyée
            </h3>

            <p className="text-gray-400 text-sm">
              Un expert Nova Communication va analyser votre situation et vous recontacter sous 24h.
            </p>

            <p className="text-gray-500 text-xs">
              Surveillez votre boîte email.
            </p>
          </div>
        ) : (

          <form onSubmit={handleSubmit} className="p-6 space-y-4">

            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nom complet"
              required
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
            />

            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email professionnel"
              required
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
            />

            <input
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Entreprise"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
            />

            <select
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
            >
              <option value="">Type de projet</option>
              <option>Acquisition marketing</option>
              <option>SEO</option>
              <option>Automatisation</option>
              <option>IA générative</option>
              <option>Refonte site</option>
              <option>Autre</option>
            </select>

            {formData.projectType === "Autre" && (
              <input
                name="projectTypeOther"
                value={formData.projectTypeOther}
                onChange={handleChange}
                placeholder="Précisez votre projet"
                required
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
              />
            )}

            <select
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white"
            >
              <option value="">Délai du projet</option>
              <option>Urgent</option>
              <option>1-3 mois</option>
              <option>3-6 mois</option>
              <option>+6 mois</option>
            </select>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg"
            >
              {loading ? "Envoi..." : "Obtenir mon audit gratuit"}
            </button>

          </form>
        )}
      </div>
    </div>
  );
}